import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CAlert, CBadge, CButton, CCard, CCardBody, CCardHeader, CCol, CFormInput, CRow, CSpinner } from '@coreui/react-pro';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { useSearchParams } from 'react-router-dom';
import * as DomicilappService from '../../services/DomicilappService';
import { DeliveryRoute, DeliveryRoutePoint, DeliveryRouteSegment } from '../../types/deliveryRoute';
import { env } from '../../Utils/Environment';
import { formatDateTime, formatTime, getApiErrorMessage } from '../orders/orderUtils';
import { CourierRouteLines, PLANNED_COLOR, plannedSummary } from '../fleet/CourierRouteLines';

// Misma clave de navegador que el mapa de flota
const MAPS_KEY = env('GOOGLE_MAPS_KEY', '');
// Igual que CourierRouteUtility::MAX_ACCURACY_METERS: lecturas peores no cuentan para la distancia
const MAX_ACCURACY_METERS = 30;
const MAP_HEIGHT = 520;

const SEGMENTS: Record<DeliveryRouteSegment['name'], { label: string; color: string }> = {
    to_store: { label: 'Hacia el comercio', color: '#F59E0B' },
    to_customer: { label: 'Hacia el cliente', color: '#3B82F6' },
};

const toLatLng = (point: DeliveryRoutePoint): google.maps.LatLngLiteral => ({ lat: Number(point.latitude), lng: Number(point.longitude) });
const isImprecise = (point: DeliveryRoutePoint) => point.accuracy !== null && point.accuracy > MAX_ACCURACY_METERS;

/* ------------------------------------------------------------------ Mapa */
const RouteMap: React.FC<{ route: DeliveryRoute }> = ({ route }) => {
    const { isLoaded, loadError } = useJsApiLoader({ id: 'domicilapp-fleet-map', googleMapsApiKey: MAPS_KEY });
    const mapRef = useRef<google.maps.Map | null>(null);
    const allPoints = useMemo(() => route.segments.flatMap((segment) => segment.points), [route]);
    const plannedPoints = useMemo(() => route.planned?.points ?? [], [route]);

    // Encuadra todo el recorrido cada vez que se carga una entrega
    useEffect(() => {
        if (!isLoaded || !mapRef.current || allPoints.length + plannedPoints.length === 0) return;
        const bounds = new google.maps.LatLngBounds();
        allPoints.forEach((point) => bounds.extend(toLatLng(point)));
        plannedPoints.forEach((point) => bounds.extend({ lat: Number(point.latitude), lng: Number(point.longitude) }));
        mapRef.current.fitBounds(bounds, 60);
    }, [isLoaded, allPoints, plannedPoints]);

    if (loadError) {
        return <div className="p-4 text-center text-body-secondary small">No se pudo cargar Google Maps. Revisa la clave REACT_APP_GOOGLE_MAPS_KEY.</div>;
    }
    if (!isLoaded) {
        return (
            <div className="d-flex align-items-center justify-content-center" style={{ height: MAP_HEIGHT }}>
                <CSpinner size="sm" className="me-2" /> <span className="text-body-secondary">Cargando mapa...</span>
            </div>
        );
    }

    const first = allPoints[0];
    const last = allPoints[allPoints.length - 1];
    const pickup = route.segments.find((segment) => segment.name === 'to_customer')?.points[0];

    return (
        <GoogleMap
            mapContainerStyle={{ width: '100%', height: MAP_HEIGHT, borderRadius: 8 }}
            center={first ? toLatLng(first) : { lat: 4.711, lng: -74.0721 }}
            zoom={14}
            onLoad={(map) => {
                mapRef.current = map;
            }}
            options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true, clickableIcons: false }}
        >
            {/* Lo que le falta según la última ruta recomendada (completa: solo SuperAdmin) */}
            <CourierRouteLines planned={route.planned} />

            {route.segments.map((segment) => (
                <Polyline
                    key={segment.name}
                    path={segment.points.map(toLatLng)}
                    options={{ strokeColor: SEGMENTS[segment.name].color, strokeOpacity: 0.9, strokeWeight: 5 }}
                />
            ))}

            {/* Lecturas imprecisas: se dibujan pero no cuentan para la distancia */}
            {allPoints.filter(isImprecise).map((point) => (
                <Marker
                    key={'imprecise-' + point.recorded_at}
                    position={toLatLng(point)}
                    title={`${formatTime(point.recorded_at)} · precisión ${Math.round(point.accuracy ?? 0)} m`}
                    icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 4, fillColor: '#94A3B8', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 1 }}
                />
            ))}

            {first && (
                <Marker
                    position={toLatLng(first)}
                    title={`Aceptó · ${formatTime(first.recorded_at)}`}
                    label={{ text: 'A', color: '#ffffff', fontSize: '11px', fontWeight: '700' }}
                    icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: '#10B981', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 }}
                />
            )}
            {pickup && (
                <Marker
                    position={toLatLng(pickup)}
                    title={`Recogió · ${formatTime(pickup.recorded_at)}`}
                    label={{ text: 'R', color: '#ffffff', fontSize: '11px', fontWeight: '700' }}
                    icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: '#FE2445', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 }}
                />
            )}
            {last && last !== first && (
                <Marker
                    position={toLatLng(last)}
                    title={`${route.in_progress ? 'Última ubicación' : 'Terminó'} · ${formatTime(last.recorded_at)}`}
                    label={{ text: route.in_progress ? '•' : 'E', color: '#ffffff', fontSize: '11px', fontWeight: '700' }}
                    icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: '#18181B', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 }}
                />
            )}
        </GoogleMap>
    );
};

/* ------------------------------------------------------------------ Resumen */
const SegmentSummary: React.FC<{ segment: DeliveryRouteSegment }> = ({ segment }) => {
    const { label, color } = SEGMENTS[segment.name];
    const imprecise = segment.points.filter(isImprecise).length;
    return (
        <div className="p-3 rounded-3 border mb-2">
            <div className="d-flex align-items-center gap-2 mb-2">
                <span className="rounded-pill d-inline-block" style={{ width: 18, height: 5, backgroundColor: color }} />
                <strong className="text-body">{label}</strong>
            </div>
            <div className="small text-body-secondary">
                {formatTime(segment.started_at)} – {formatTime(segment.ended_at)} · {segment.duration_minutes} min · {segment.distance_km.toFixed(2)} km
            </div>
            <div className="small text-body-secondary">
                {segment.points.length} puntos{imprecise > 0 ? ` · ${imprecise} imprecisos (> ${MAX_ACCURACY_METERS} m)` : ''}
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ Página */
export const DeliveryRouteAdministrator: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [deliveryId, setDeliveryId] = useState(searchParams.get('delivery_id') ?? '');
    const [route, setRoute] = useState<DeliveryRoute | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = async (id: string) => {
        const numericId = Number(id);
        if (!numericId) return;
        setLoading(true);
        setError(null);
        setRoute(null);
        setSearchParams({ delivery_id: String(numericId) }, { replace: true });
        try {
            const response = await DomicilappService.getDeliveryRoute(numericId);
            if (response?.status === 200) {
                setRoute(response.data);
            } else {
                setError(response?.message ?? 'No fue posible cargar el recorrido');
            }
        } catch (err) {
            setError(getApiErrorMessage(err, 'No fue posible cargar el recorrido'));
        } finally {
            setLoading(false);
        }
    };

    // Enlace directo: /admin/cpanel/routes?delivery_id=123
    useEffect(() => {
        if (deliveryId) load(deliveryId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pointsCount = route?.segments.reduce((total, segment) => total + segment.points.length, 0) ?? 0;

    return (
        <CCard className="mb-4">
            <CCardHeader>
                <strong>Recorrido de entregas</strong>
                <div className="small text-body-secondary">
                    Recorrido real del domiciliario desde que acepta hasta que entrega. Solo para soporte: los puntos se borran a los 90 días.
                </div>
            </CCardHeader>
            <CCardBody>
                <form
                    className="d-flex gap-2 mb-3"
                    style={{ maxWidth: 360 }}
                    onSubmit={(event) => {
                        event.preventDefault();
                        load(deliveryId);
                    }}
                >
                    <CFormInput type="number" min={1} placeholder="ID de la entrega" value={deliveryId} onChange={(event) => setDeliveryId(event.target.value)} />
                    <CButton type="submit" color="dark" disabled={loading || !Number(deliveryId)}>
                        {loading ? <CSpinner size="sm" /> : 'Ver'}
                    </CButton>
                </form>

                {error && <CAlert color="danger">{error}</CAlert>}

                {route && (
                    <CRow className="g-3">
                        <CCol lg={8}>
                            {!MAPS_KEY ? (
                                <div className="p-4 text-center text-body-secondary small">
                                    Mapa no configurado: agrega <code>REACT_APP_GOOGLE_MAPS_KEY_DEV</code> / <code>_PROD</code>.
                                </div>
                            ) : pointsCount === 0 && !route.planned?.points.length ? (
                                <div className="d-flex align-items-center justify-content-center text-center text-body-secondary small border rounded-3" style={{ height: MAP_HEIGHT }}>
                                    No hay puntos guardados para esta entrega.<br />
                                    Puede ser anterior al historial de recorridos o tener más de 90 días.
                                </div>
                            ) : (
                                <RouteMap route={route} />
                            )}
                        </CCol>
                        <CCol lg={4}>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <h5 className="mb-0">Entrega #{route.delivery_id}</h5>
                                {route.in_progress ? <CBadge color="info">En curso</CBadge> : <CBadge color="success">Terminada</CBadge>}
                            </div>
                            <div className="small mb-3">
                                <div><span className="text-body-secondary">Domiciliario:</span> {route.courier_name} (#{route.courier_id})</div>
                                <div><span className="text-body-secondary">Aceptó:</span> {formatDateTime(route.started_at)}</div>
                                <div><span className="text-body-secondary">Recogió:</span> {formatDateTime(route.picked_up_at)}</div>
                                <div><span className="text-body-secondary">Terminó:</span> {formatDateTime(route.finished_at)}</div>
                                <div><span className="text-body-secondary">Distancia total:</span> <strong>{route.distance_km.toFixed(2)} km</strong></div>
                            </div>
                            {route.segments.map((segment) => (
                                <SegmentSummary key={segment.name} segment={segment} />
                            ))}
                            {route.planned && (
                                <div className="p-3 rounded-3 border mb-2">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <span className="d-inline-block" style={{ width: 18, borderTop: `3px dashed ${PLANNED_COLOR}` }} />
                                        <strong className="text-body">Ruta recomendada</strong>
                                    </div>
                                    <div className="small text-body-secondary">{plannedSummary(route.planned).estimate || '—'}</div>
                                    <div className="small text-body-secondary">{plannedSummary(route.planned).computedAgo}</div>
                                </div>
                            )}
                            <div className="small text-body-secondary mt-2">
                                A = aceptó · R = recogió · E = entregó. Los puntos grises son lecturas imprecisas. La línea punteada es la ruta que le recomendó la app.
                            </div>
                        </CCol>
                    </CRow>
                )}
            </CCardBody>
        </CCard>
    );
};

export default DeliveryRouteAdministrator;
